/**
 * TopBar — 56px header. Logo + surface toggle (+ Builder tab for softknack),
 * role toggle, and either the tenant badge (directory) or the internal badge
 * (builder). Verbatim layout + tokens.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii, shadows } from '../theme';
import { BridgeMark, Lock } from '../icons';
import { useBridge } from '../store/bridgeStore';
import { useTheme } from '../context/themeContext';
import { useSession } from './SessionGate';
import { logout } from '../api/session';
import { USE_PLATFORM_AUTH } from '../api/config';

export default function TopBar() {
  const { state, actions } = useBridge();
  const session = useSession();
  // The real signed-in org. Only falls back when running in dev-bypass mode,
  // where there is no platform session to name.
  const tenantName = session?.org?.name ?? 'Local dev tenant';
  // Light/dark toggle: the theme system + tokens already existed (themeContext),
  // but the production TopBar never surfaced a control (only the unused BootShell
  // did). Wire it here so users can flip themes from the real app.
  const { isDark, toggleTheme } = useTheme();
  const isSoftknack = state.role === 'softknack';
  const isDirectory = state.surface === 'directory';

  return (
    <View style={styles.header}>
      <View style={styles.brandRow}>
        <View style={styles.logo}><BridgeMark size={16} color="#fff" /></View>
        <Text style={styles.brandText}>SK Bridge</Text>
      </View>

      {/* surface toggle */}
      <View style={styles.segGroup}>
        <Seg label="Connectors Directory" active={isDirectory} onPress={() => actions.setSurface('directory')} />
        {isSoftknack && (
          <Seg label="AI Integration Builder" active={state.surface === 'builder'} onPress={() => actions.setSurface('builder')} />
        )}
        {isSoftknack && (
          <Seg label="Ops" active={state.surface === 'ops'} onPress={() => actions.setSurface('ops')} />
        )}
      </View>

      <View style={{ flex: 1 }} />

      {/* role toggle */}
      <View style={styles.roleGroup}>
        <Role label="Softknack admin" active={isSoftknack} onPress={() => actions.setRole('softknack')} />
        <Role label="Tenant" active={!isSoftknack} onPress={() => actions.setRole('tenant')} />
      </View>

      {isDirectory ? (
        <View style={styles.tenantRow}>
          <Text style={styles.tenantLabel}>Tenant</Text>
          <View style={styles.tenantPill}>
            <View style={styles.tenantAvatar}>
              <Text style={styles.tenantAvatarText}>{(tenantName[0] ?? '?').toUpperCase()}</Text>
            </View>
            <Text style={styles.tenantName}>{tenantName}</Text>
          </View>
        </View>
      ) : (
        <View style={styles.internal}>
          <Lock size={11} color={colors.paper} />
          <Text style={styles.internalText}>Softknack internal</Text>
        </View>
      )}

      {/* Light/dark theme toggle — flips the OKLCH token set instantly. */}
      <Pressable onPress={toggleTheme} style={styles.themeBtn} title="Toggle light / dark theme">
        <Text style={styles.themeBtnText}>{isDark ? '☾ dark' : '☀ light'}</Text>
      </Pressable>

      {/* Sign out clears the platform session cookie, so it only exists when
          there is a real session to clear. */}
      {USE_PLATFORM_AUTH && session ? (
        <Pressable
          onPress={logout}
          style={styles.themeBtn}
          {...({ title: `Sign out ${session.user?.email ?? ''}`.trim() } as object)}
        >
          <Text style={styles.themeBtnText}>sign out</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function Seg({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={[styles.seg, active ? styles.segActive : null]}>
      <Text style={[styles.segText, active ? styles.segActiveText : null]}>{label}</Text>
    </Pressable>
  );
}
function Role({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={[styles.role, active ? styles.roleActive : null]}>
      <Text style={[styles.roleText, active ? styles.roleActiveText : null]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  header: {
    height: 56, flexShrink: 0, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: colors.card,
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, gap: 16,
  },
  brandRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  logo: { width: 28, height: 28, borderRadius: 7, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center' },
  brandText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink, letterSpacing: -0.15 },

  segGroup: { flexDirection: 'row', gap: 4, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, padding: 3 },
  seg: { borderRadius: radii.r2, paddingVertical: 6, paddingHorizontal: 14, cursor: 'pointer' } as any,
  segActive: { backgroundColor: colors.card, boxShadow: shadows.sm } as any,
  segText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink3 },
  segActiveText: { color: colors.ink },

  roleGroup: { flexDirection: 'row', gap: 3, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: radii.full, padding: 3, marginRight: 4 },
  role: { borderRadius: radii.full, paddingVertical: 5, paddingHorizontal: 12, cursor: 'pointer' } as any,
  roleActive: { backgroundColor: colors.ink },
  roleText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: colors.ink3 },
  roleActiveText: { color: colors.paper as any },

  tenantRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  tenantLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink3 },
  tenantPill: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4, paddingRight: 10, paddingLeft: 4, borderWidth: 1, borderColor: colors.line, borderRadius: radii.full, backgroundColor: colors.paper2 },
  tenantAvatar: { width: 24, height: 24, borderRadius: 12, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center' },
  tenantAvatarText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: '#fff' },
  tenantName: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink },

  internal: { flexDirection: 'row', alignItems: 'center', gap: 6, height: 24, paddingHorizontal: 10, borderRadius: radii.full, backgroundColor: colors.ink },
  internalText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: colors.paper as any },

  // Theme toggle pill — mirrors the style BootShell used (mono label, bordered pill).
  themeBtn: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.full, paddingVertical: 5, paddingHorizontal: 10, cursor: 'pointer' } as any,
  themeBtnText: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink2 },
});
